import React, { useCallback, useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { useNavigate } from "react-router-dom";
import { zodResolver } from "@hookform/resolvers/zod";
import { zodChangeAccount } from "@/libs/zod/zodValidation";
import { useUserStore } from "@/store/useUserStore";
import { useMutation } from "@tanstack/react-query";
import { useQueryClient } from "@tanstack/react-query";
import Interceptor from "@/libs/axios/AxiosInterceptor";

import { color } from "@/theme";
import {
  CommonContainer,
  CommonPageContainer,
} from "@/components/ui/container";
import { CommonTitleTwo } from "@/components/ui/fonts/Fonts";
import { BirthField, EmailField, NameField } from "@/components/ui/form/Fields";
import { PhoneVerificationFields, SingleCheckBox } from "@/components/ui/form";
import { USER_API_URL } from "@/constants/apiUrls";
import {
  DUPLICATE_USER_PHONE,
  SUCCESS_CODE,
} from "@/constants/responseResults";
import { LOCAL_STORAGE_TOKENS } from "@/constants/storageKey";
import { USER_DATA_QUERY_KEY } from "@/constants/queryKeys";
import { PrimaryButton } from "@/components/ui/buttons";
import {
  changeBirthFormat,
  changePhoneFormat,
  replaceAllDash,
} from "@/utils/Functions";
import { FormContainer, SubmitButtonWrapper } from "@/_root/pages/user/Login";
import { NameBirthContainer } from "@/_root/pages/user/Join";
import { PHONE_ALREADY_EXISTS } from "@/constants/inputErrorMessage";

const ChangeAccount = () => {
  const { user, setUser } = useUserStore();
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const [phoneChanged, setPhoneChanged] = useState(false);

  const { control, handleSubmit, watch, setValue, setError } = useForm({
    resolver: zodResolver(zodChangeAccount),
    defaultValues: {
      user_email: user?.user_email,
      user_birth: "",
      user_name: "",
      user_phone: "",
      email_receive_yn: false,
      phone_changed: phoneChanged,
    },
  });

  useEffect(() => {
    if (user) {
      setValue("user_email", user.user_email);
      setValue("user_name", user.user_name);
      setValue("user_phone", changePhoneFormat(user.user_phone));
      setValue("user_birth", changeBirthFormat(user.user_birth));
      setValue("email_receive_yn", user.email_receive_yn === 1);
    }
  }, [user, setValue]);

  const { mutate: updateUserAccount } = useMutation({
    mutationFn: async data => {
      const body = {
        user_name: data?.user_name,
        user_birth: replaceAllDash(data?.user_birth),
        email_receive_yn: data?.email_receive_yn ? 1 : 0,
        ...(phoneChanged
          ? { user_phone: replaceAllDash(data?.user_phone) }
          : {}),
      };

      return await Interceptor.patch(USER_API_URL, body);
    },
    onSuccess: (data, variables) => {
      const refreshToken = JSON.parse(
        localStorage.getItem(LOCAL_STORAGE_TOKENS),
      ).refresh_token;

      const newToken = JSON.stringify({
        access_token: data?.data.data.access_token,
        refresh_token: refreshToken,
      });

      localStorage.setItem(LOCAL_STORAGE_TOKENS, newToken);

      if (data?.data?.code === SUCCESS_CODE) {
        // NOTE: 응답 객체(axios response) 대신, 방금 제출한 값으로 store를 즉시 동기화
        setUser({
          ...(user ?? {}),
          user_name: variables?.user_name,
          user_birth: replaceAllDash(variables?.user_birth),
          user_phone: replaceAllDash(variables?.user_phone),
          email_receive_yn: variables?.email_receive_yn ? 1 : 0,
          login_type: user?.login_type,
        });

        // Header 등 다른 화면에서도 최신 유저로 재동기화
        queryClient.invalidateQueries({ queryKey: [USER_DATA_QUERY_KEY] });

        navigate("/mypage", { state: { changeAccountSuccess: true } });
      }
    },
    onError: error => {
      console.log(error);

      if (error?.response?.data?.code === DUPLICATE_USER_PHONE) {
        setError("user_phone", { message: PHONE_ALREADY_EXISTS });
      }
    },
  });

  useEffect(() => {
    const subscription = watch(value => {
      if (value.user_phone !== changePhoneFormat(user.user_phone)) {
        setPhoneChanged(true);
      } else {
        setPhoneChanged(false);
      }
    });

    return () => subscription.unsubscribe();
  }, [setValue, user, watch]);

  useEffect(() => {
    if (phoneChanged) {
      setValue("phone_changed", true);
    } else {
      setValue("phone_changed", false);
    }
  }, [phoneChanged, setValue]);

  const onSubmit = useCallback(
    async data => {
      updateUserAccount({ ...data, phone_changed: phoneChanged });
    },
    [updateUserAccount, phoneChanged],
  );

  return (
    <CommonPageContainer>
      <CommonContainer>
        <CommonTitleTwo>프로필 정보 수정</CommonTitleTwo>

        <FormContainer onSubmit={handleSubmit(onSubmit)}>
          <div>
            <div>
              <EmailField control={control} disabled />

              <NameBirthContainer justify="space-between" gap="1rem">
                <NameField control={control} />

                <BirthField control={control} setValue={setValue} />
              </NameBirthContainer>

              <PhoneVerificationFields control={control} setValue={setValue} />

              <SingleCheckBox
                name="email_receive_yn"
                control={control}
                label="이메일 수신동의"
              />
            </div>
          </div>

          <SubmitButtonWrapper justify="center">
            <PrimaryButton bgColor={color.secondary02} buttonType="submit">
              수정하기
            </PrimaryButton>
          </SubmitButtonWrapper>
        </FormContainer>
      </CommonContainer>
    </CommonPageContainer>
  );
};

export default ChangeAccount;
